/* Card de recepciones pendientes (entregas masivas) para la pantalla de Inicio.
 *
 * - Muestra un resumen clickeable de las transferencias dirigidas que el
 *   usuario tiene pendientes de aceptar (en cualquier tema vigente).
 * - Al tocar, abre un modal con el detalle de cada item y permite aceptarlas o
 *   rechazarlas individualmente, o todas a la vez.
 */
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonModal,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { Typography } from "@material-ui/core";
import { useEffect, useState } from "react";

import {
  aceptarTransferenciaDirecta,
  obtenerRecepcionesPendientes,
  rechazarTransferencia,
  RecepcionPendiente,
} from "src/services/entregasMasivas";

const itemLabel = (t: RecepcionPendiente): string => {
  if (t.identificador) {
    return `${t.categoria || "Item"} · N° ${t.identificador}`;
  }
  if (t.categoria) {
    return `${t.cantidad} × ${t.categoria}`;
  }
  return `${t.cantidad} unidad(es)`;
};

const EntregasRecepcionesCard: React.FC = () => {
  const [present] = useIonToast();
  const [items, setItems] = useState<RecepcionPendiente[]>([]);
  const [cargando, setCargando] = useState(true);
  const [abierto, setAbierto] = useState(false);
  const [procesando, setProcesando] = useState<number | null>(null);
  const [procesandoTodos, setProcesandoTodos] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      setItems(await obtenerRecepcionesPendientes());
    } catch {
      setItems([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  const aceptar = async (t: RecepcionPendiente) => {
    setProcesando(t.id);
    try {
      await aceptarTransferenciaDirecta(t.id);
      present({ message: `Recibiste: ${itemLabel(t)}`, duration: 3000, color: "success" });
      await cargar();
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "No se pudo aceptar",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setProcesando(null);
    }
  };

  const rechazar = async (t: RecepcionPendiente) => {
    setProcesando(t.id);
    try {
      await rechazarTransferencia(t.id);
      present({ message: "Recepción rechazada", duration: 2500, color: "medium" });
      await cargar();
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "No se pudo rechazar",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setProcesando(null);
    }
  };

  // Procesa todas en secuencia (aceptar o rechazar) y resume el resultado.
  const procesarTodas = async (accion: "aceptar" | "rechazar") => {
    const lista = [...items];
    if (!lista.length) return;
    setProcesandoTodos(true);
    let ok = 0;
    const errores: string[] = [];
    for (const t of lista) {
      try {
        if (accion === "aceptar") await aceptarTransferenciaDirecta(t.id);
        else await rechazarTransferencia(t.id);
        ok++;
      } catch (err: any) {
        errores.push(`${itemLabel(t)}: ${err?.response?.data?.error || "error"}`);
      }
    }
    setProcesandoTodos(false);
    const verbo = accion === "aceptar" ? "aceptada(s)" : "rechazada(s)";
    present({
      message:
        errores.length === 0
          ? `${ok} ${verbo}`
          : `${ok} ${verbo}, ${errores.length} con error`,
      duration: 4000,
      color: errores.length === 0 ? "success" : "warning",
    });
    await cargar();
  };

  if (cargando) return null;
  if (!items.length) return null;

  const bloqueado = procesandoTodos || procesando != null;

  return (
    <div className="container mt-1">
      <div
        onClick={() => setAbierto(true)}
        style={{
          cursor: "pointer",
          background: "#fff",
          borderRadius: 10,
          borderLeft: "6px solid var(--garnet, #910434)",
          boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
          padding: "12px 14px",
          marginBottom: 10,
        }}
      >
        <Typography variant="caption" style={{ color: "var(--garnet, #910434)", fontWeight: 700 }}>
          📦 RECEPCIONES PENDIENTES
        </Typography>
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          Tenés {items.length} item(s) por recibir
        </Typography>
        <Typography variant="caption" style={{ color: "var(--garnet, #910434)", fontWeight: 600 }}>
          Tocá para revisar y aceptar →
        </Typography>
      </div>

      <IonModal isOpen={abierto} onDidDismiss={() => setAbierto(false)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle style={{ fontSize: 16 }}>Recepciones pendientes</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setAbierto(false)}>Cerrar</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div style={{ padding: 16 }}>
            {items.length > 1 && (
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <IonButton
                  expand="block"
                  color="primary"
                  style={{ flex: 1 }}
                  disabled={bloqueado}
                  onClick={() => procesarTodas("aceptar")}
                >
                  {procesandoTodos ? <IonSpinner name="dots" /> : "Aceptar todas"}
                </IonButton>
                <IonButton
                  expand="block"
                  fill="outline"
                  color="medium"
                  style={{ flex: 1 }}
                  disabled={bloqueado}
                  onClick={() => procesarTodas("rechazar")}
                >
                  Rechazar todas
                </IonButton>
              </div>
            )}

            {items.map((t) => (
              <div
                key={t.id}
                style={{
                  background: "#fafafa",
                  border: "1px solid #eee",
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 10,
                }}
              >
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  {itemLabel(t)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  De <strong>{t.nombre_origen || t.cedula_origen}</strong>
                  {t.tema_nombre ? ` · ${t.tema_nombre}` : ""}
                </Typography>
                {(t.punto_servicio_nombre || t.punto_servicio) && (
                  <Typography variant="body2" style={{ color: "#00695c" }}>
                    📍 {t.punto_servicio_nombre || t.punto_servicio}
                  </Typography>
                )}
                {t.motivo && (
                  <Typography variant="caption" color="textSecondary">
                    {t.motivo}
                  </Typography>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <IonButton
                    size="small"
                    color="primary"
                    disabled={bloqueado}
                    onClick={() => aceptar(t)}
                  >
                    {procesando === t.id ? <IonSpinner name="dots" /> : "Aceptar"}
                  </IonButton>
                  <IonButton
                    size="small"
                    fill="outline"
                    color="medium"
                    disabled={bloqueado}
                    onClick={() => rechazar(t)}
                  >
                    Rechazar
                  </IonButton>
                </div>
              </div>
            ))}
          </div>
        </IonContent>
      </IonModal>
    </div>
  );
};

export default EntregasRecepcionesCard;
